// src/client/services/soundEffects.ts
// Professional office-appropriate audio notifications & haptic feedback

let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!audioCtx) {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (AudioContextClass) {
      audioCtx = new AudioContextClass();
    }
  }
  if (audioCtx && audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => {});
  }
  return audioCtx;
}

// Ensure audio context is unmuted on initial mobile touch/click
if (typeof window !== 'undefined') {
  const unlockAudio = () => {
    const ctx = getAudioContext();
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    if (ctx) {
      try {
        const buffer = ctx.createBuffer(1, 1, 22050);
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.start(0);
      } catch (e) {}
    }
  };

  window.addEventListener('touchstart', unlockAudio, { once: true, passive: true });
  window.addEventListener('touchend', unlockAudio, { once: true, passive: true });
  window.addEventListener('click', unlockAudio, { once: true, passive: true });
  window.addEventListener('keydown', unlockAudio, { once: true, passive: true });
}

/**
 * Professional Office Notification Chime
 * Subtle, warm, 2-note acoustic bell (C5 523Hz + G5 784Hz) with lowpass warmth.
 * No frequency sweeps, no whistling, non-intrusive for quiet office environments.
 */
export function playIncomingMessageSound(): void {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;

    // Master filter to remove any harsh treble or whistle frequencies
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(1400, now);
    filter.connect(ctx.destination);

    // Note 1: Warm fundamental (C5: 523.25 Hz)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(523.25, now); // Steady fixed pitch (no slide)

    gain1.gain.setValueAtTime(0.001, now);
    gain1.gain.linearRampToValueAtTime(0.24, now + 0.02);
    gain1.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);

    osc1.connect(gain1);
    gain1.connect(filter);

    // Note 2: Harmonic fifth (G5: 783.99 Hz) arriving 70ms later
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(783.99, now + 0.07); // Steady fixed pitch (no slide)

    gain2.gain.setValueAtTime(0.0001, now + 0.07);
    gain2.gain.linearRampToValueAtTime(0.20, now + 0.09);
    gain2.gain.exponentialRampToValueAtTime(0.0001, now + 0.38);

    osc2.connect(gain2);
    gain2.connect(filter);

    osc1.start(now);
    osc1.stop(now + 0.30);

    osc2.start(now + 0.07);
    osc2.stop(now + 0.40);

    // Subtle single pulse haptic feedback for smartphones
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate(35);
    }
  } catch (err) {
    console.warn('[soundEffects] Incoming notification sound error:', err);
  }
}

/**
 * Subtle Outgoing Sent Confirmation Click
 * Muted, warm, tactile feedback (380 Hz)
 */
export function playOutgoingMessageSound(): void {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(380, now);

    gain.gain.setValueAtTime(0.001, now);
    gain.gain.linearRampToValueAtTime(0.12, now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + 0.06);

    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate(15);
    }
  } catch (err) {
    console.warn('[soundEffects] Outgoing sent sound error:', err);
  }
}

/**
 * Dispatches a native browser notification with click handling
 */
export function showBrowserNotification(
  title: string,
  options?: NotificationOptions,
  onClick?: () => void
): void {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;

  try {
    const notif = new Notification(title, {
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      ...options,
    });

    if (onClick) {
      notif.onclick = () => {
        window.focus();
        onClick();
        notif.close();
      };
    }
  } catch (e) {
    console.warn('[soundEffects] Failed to show browser notification:', e);
  }
}

// ============================================================================
// DISTINCT PROFESSIONAL CALL RINGTONES (CALLER RINGBACK vs. CALLEE RINGTONE)
// ============================================================================

let outgoingRingInterval: ReturnType<typeof setInterval> | null = null;
let incomingRingInterval: ReturnType<typeof setInterval> | null = null;
let activeRingNodes: Array<{ osc: OscillatorNode; gain: GainNode }> = [];

function clearActiveRingNodes() {
  for (const item of activeRingNodes) {
    try {
      item.gain.gain.cancelScheduledValues(0);
      item.gain.gain.setValueAtTime(0.0001, audioCtx?.currentTime || 0);
      item.osc.stop();
      item.osc.disconnect();
    } catch {}
  }
  activeRingNodes = [];
}

/**
 * Plays a single cycle of the Outgoing Caller Ringback Tone (heard by the person calling).
 * Classic warm dual-frequency telecom ringback (440 Hz + 480 Hz) with a modern double-pulse cadence.
 */
function playOutgoingRingbackCycle() {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(1100, now);
    filter.connect(ctx.destination);

    // Two gentle pulses: [0.0s -> 0.45s] and [0.65s -> 1.15s]
    const pulses = [
      { start: 0, duration: 0.45 },
      { start: 0.65, duration: 0.50 },
    ];

    for (const p of pulses) {
      for (const freq of [440, 480]) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now + p.start);

        gain.gain.setValueAtTime(0.0001, now + p.start);
        gain.gain.linearRampToValueAtTime(0.085, now + p.start + 0.04);
        gain.gain.setValueAtTime(0.085, now + p.start + p.duration - 0.05);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + p.start + p.duration);

        osc.connect(gain);
        gain.connect(filter);

        osc.start(now + p.start);
        osc.stop(now + p.start + p.duration + 0.02);
        activeRingNodes.push({ osc, gain });
      }
    }
  } catch (err) {
    console.warn('[soundEffects] Outgoing ringback error:', err);
  }
}

/**
 * Plays a single cycle of the Incoming Callee Ringtone (heard by the person being called).
 * Distinct, melodic, professional workspace marimba/chime arpeggio (E5 - G#5 - B5 - E6 - B5 - E6).
 */
function playIncomingRingtoneCycle() {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2000, now);
    filter.connect(ctx.destination);

    // Distinct melodic chime pattern
    const notes = [
      { freq: 659.25, offset: 0.0, dur: 0.28, vol: 0.16 },   // E5
      { freq: 830.61, offset: 0.18, dur: 0.28, vol: 0.16 },  // G#5
      { freq: 987.77, offset: 0.36, dur: 0.30, vol: 0.17 },  // B5
      { freq: 1318.51, offset: 0.56, dur: 0.45, vol: 0.18 }, // E6
      { freq: 987.77, offset: 0.92, dur: 0.25, vol: 0.14 },  // B5
      { freq: 1318.51, offset: 1.12, dur: 0.55, vol: 0.19 }, // E6
    ];

    for (const n of notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(n.freq, now + n.offset);

      gain.gain.setValueAtTime(0.0001, now + n.offset);
      gain.gain.linearRampToValueAtTime(n.vol, now + n.offset + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + n.offset + n.dur);

      osc.connect(gain);
      gain.connect(filter);

      osc.start(now + n.offset);
      osc.stop(now + n.offset + n.dur + 0.02);
      activeRingNodes.push({ osc, gain });
    }

    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate([120, 80, 120]);
    }
  } catch (err) {
    console.warn('[soundEffects] Incoming ringtone error:', err);
  }
}

export function startOutgoingCallerRingtone(): void {
  stopOutgoingCallerRingtone();
  playOutgoingRingbackCycle();
  outgoingRingInterval = setInterval(() => {
    playOutgoingRingbackCycle();
  }, 3200);
}

export function stopOutgoingCallerRingtone(): void {
  if (outgoingRingInterval) {
    clearInterval(outgoingRingInterval);
    outgoingRingInterval = null;
  }
  clearActiveRingNodes();
}

export function startIncomingCalleeRingtone(): void {
  stopIncomingCalleeRingtone();
  playIncomingRingtoneCycle();
  incomingRingInterval = setInterval(() => {
    playIncomingRingtoneCycle();
  }, 2600);
}

export function stopIncomingCalleeRingtone(): void {
  if (incomingRingInterval) {
    clearInterval(incomingRingInterval);
    incomingRingInterval = null;
  }
  clearActiveRingNodes();
}

export function stopAllCallRingtones(): void {
  stopOutgoingCallerRingtone();
  stopIncomingCalleeRingtone();
}

